import { FileDownload, MoreVert as MoreVertIcon, PeopleAlt, Visibility } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import ChipLabel, { type CHIP_COLORS } from '/@/components/chip-label';
import { type ActivityListItem, useActivityPdfDownload } from '/@/hooks/activities';
import { useAllUserNodesLookup } from '/@/hooks/nodes';
import { useIsValidatedSquad } from '/@/hooks/session';
import { _, getActiveLanguage } from '/@/locale';
import { getActivityTypeLabel, getModalityLabel } from '../../constants';
import ActivityDetailModal from '../activity-detail-modal';
import DeleteActivityModal from '../delete-activity-modal';

const getDateParts = (dateStr: string) => {
    const date = new Date(dateStr);
    const language = getActiveLanguage();
    const languages: Record<string, string> = { es: 'es-AR', pt: 'pt-BR', en: 'en-US' };
    return {
        day: date.toLocaleDateString(languages[language] || language, { timeZone: 'UTC', day: 'numeric' }),
        month: date
            .toLocaleDateString(languages[language] || language, { timeZone: 'UTC', month: 'short' })
            .replace('.', ''),
    };
};

const ActivityCard = ({ activity, onEdit }: { activity: ActivityListItem; onEdit: (activityId: number) => void }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const { allNodesById } = useAllUserNodesLookup();
    const isValidatedSquad = useIsValidatedSquad();
    const { download: downloadPdf, isDownloading } = useActivityPdfDownload(activity.id);

    const ownerName = allNodesById.get(activity.userNode)?.name || '-';
    const ownerType = allNodesById.get(activity.userNode)?.type || 'gray';
    const { day, month } = getDateParts(activity.date);
    const attended = activity.attendanceSummary?.si ?? 0;

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        downloadPdf();
    };

    return (
        <Box
            sx={{
                bgcolor: 'white',
                border: '1px solid rgba(16, 56, 79, 0.12)',
                borderRadius: '16px',
                boxShadow:
                    '0px 1px 0.5px rgba(0,0,0,0.14), 0px 2px 0.5px rgba(0,0,0,0.12), 0px 1px 1.5px rgba(0,0,0,0.2)',
                p: { xs: 1, md: 3 },
                display: 'flex',
                gap: 2.5,
                alignItems: 'center',
                width: '100%',
                cursor: 'pointer',
            }}
            onClick={() => setOpenDetailModal(true)}
        >
            <Stack alignItems='center' justifyContent='space-between' sx={{ width: '64px', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '34px', lineHeight: '40px', fontWeight: 500, color: '#006F9B' }}>
                    {day}
                </Typography>
                <Typography
                    variant='caption'
                    color='primary.mui.inactive'
                    sx={{
                        letterSpacing: '1.25px',
                        textTransform: 'uppercase',
                    }}
                >
                    {month}
                </Typography>
            </Stack>
            <Box sx={{ width: '1px', height: '48px', bgcolor: 'rgba(16, 56, 79, 0.12)', flexShrink: 0 }} />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                    <Typography
                        variant='caption'
                        color='primary.mui.inactive'
                        sx={{
                            letterSpacing: '1.25px',
                            textTransform: 'uppercase',
                        }}
                    >
                        {getActivityTypeLabel(activity.activityType)} | {getModalityLabel(activity.modality)}
                    </Typography>
                </Stack>
                <Typography variant='subtitle1' fontWeight={500} color='primary' sx={{ fontSize: '20px' }}>
                    {activity.name}
                </Typography>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                    <ChipLabel type={ownerType as keyof typeof CHIP_COLORS} label={ownerName} />
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                        <PeopleAlt sx={{ width: 16, height: 16, color: 'primary.onSurface' }} />
                        <Typography variant='caption' color='primary.mui.inactive' fontWeight={500}>
                            {_('{{attended}}/{{total}} asistieron', {
                                attended,
                                total: activity.participantsCount,
                            })}
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
            {isValidatedSquad ? (
                <Stack direction='row' alignItems='center' sx={{ flexShrink: 0 }}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenDetailModal(true);
                        }}
                    >
                        <Visibility color='primary' />
                    </IconButton>
                    <IconButton onClick={handleDownload} disabled={isDownloading} sx={{ mr: -1 }}>
                        {isDownloading ? (
                            <CircularProgress size={20} color='primary' />
                        ) : (
                            <FileDownload color='primary' />
                        )}
                    </IconButton>
                </Stack>
            ) : (
                <Box sx={{ flexShrink: 0 }}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                        }}
                        sx={{ mr: -1 }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        open={!!anchorEl}
                        onClose={(e: React.MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            setAnchorEl(null);
                        }}
                        anchorEl={anchorEl}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                    >
                        <MenuItem
                            onClick={() => {
                                setOpenDetailModal(true);
                                setAnchorEl(null);
                            }}
                        >
                            {_('Ver detalle')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                onEdit(activity.id);
                                setAnchorEl(null);
                            }}
                        >
                            {_('Editar')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                downloadPdf();
                                setAnchorEl(null);
                            }}
                            disabled={isDownloading}
                        >
                            {isDownloading ? _('Generando PDF...') : _('Descargar')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                setOpenDeleteModal(true);
                                setAnchorEl(null);
                            }}
                        >
                            {_('Eliminar')}
                        </MenuItem>
                    </Menu>
                    <DeleteActivityModal
                        activityId={activity.id}
                        activityName={activity.name}
                        open={openDeleteModal}
                        setOpen={setOpenDeleteModal}
                    />
                </Box>
            )}
            <ActivityDetailModal
                activityId={activity.id}
                open={openDetailModal}
                onClose={() => setOpenDetailModal(false)}
            />
        </Box>
    );
};

export default ActivityCard;
